import { useState, useEffect, useCallback } from 'react';
import './TypeWriter.css';

const AdvancedTypeWriter = ({ 
  sequences = [], 
  typeSpeed = 100, 
  deleteSpeed = 50, 
  delayBetweenSequences = 1000,
  className = '',
  cursorClassName = '',
  loop = true 
}) => {
  const [displayText, setDisplayText] = useState('');
  const [sequenceIndex, setSequenceIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPaused] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  const handleTypingComplete = useCallback(() => {
    setIsComplete(true);
    
    const currentSequence = sequences[sequenceIndex];
    const currentSequenceObj = typeof currentSequence === 'string' ? 
      { deleteDelay: delayBetweenSequences, deleteText: true } : 
      currentSequence;
    
    const delay = currentSequenceObj.deleteDelay || delayBetweenSequences;
    
    if (currentSequenceObj.deleteText !== false) {
      setTimeout(() => {
        setIsDeleting(true);
        setIsComplete(false);
      }, delay);
    } else {
      setTimeout(() => {
        setSequenceIndex(prev => (prev + 1) % sequences.length);
        setCharIndex(0);
        setDisplayText('');
        setIsComplete(false);
      }, delay);
    }
  }, [sequenceIndex, sequences, delayBetweenSequences]);

  useEffect(() => {
    if (sequences.length === 0) return;
    
    if (sequenceIndex >= sequences.length) {
      if (!loop) return;
      
      setSequenceIndex(0);
      setCharIndex(0);
      setIsDeleting(false);
      setDisplayText('');
      setIsComplete(false);
      return;
    }

    if (isPaused || isComplete) return;

    const currentSequence = sequences[sequenceIndex];
    const currentText = typeof currentSequence === 'string' ? 
      currentSequence : 
      currentSequence.text;
    
    const speed = isDeleting ? 
      (typeof currentSequence === 'string' ? deleteSpeed : (currentSequence.deleteSpeed || deleteSpeed)) : 
      (typeof currentSequence === 'string' ? typeSpeed : (currentSequence.typeSpeed || typeSpeed));
    
    const timer = setTimeout(() => {
      if (!isDeleting) {
        if (charIndex < currentText.length) {
          setDisplayText(currentText.substring(0, charIndex + 1));
          setCharIndex(charIndex + 1);
        } 
        else if (charIndex === currentText.length) {
          handleTypingComplete();
        }
      }
      else {
        if (charIndex > 0) {
          setDisplayText(currentText.substring(0, charIndex - 1));
          setCharIndex(charIndex - 1);
        } 
        else {
          setIsDeleting(false);
          setSequenceIndex((prevIndex) => {
            const nextIndex = prevIndex + 1;
            if (nextIndex >= sequences.length) {
              return loop ? 0 : prevIndex;
            }
            return nextIndex;
          });
        }
      }
    }, speed);
    
    return () => clearTimeout(timer);
  }, [
    charIndex, 
    delayBetweenSequences, 
    deleteSpeed, 
    handleTypingComplete,
    isComplete,
    isDeleting, 
    isPaused, 
    loop, 
    sequenceIndex, 
    sequences, 
    typeSpeed
  ]);

  const cursorClass = `typewriter-cursor ${cursorClassName}`.trim();
  
  return (
    <span className={`typewriter-container ${className}`.trim()}>
      <span className="typewriter-text">{displayText}</span>
      <span className={cursorClass}>|</span>
    </span>
  );
};

export default AdvancedTypeWriter;